const ANIMATION_DURATION_DESKTOP_MENU = 300;
const ANIMATION_DURATION_MOBILE_DRAWER = 400;
const HOVER_TIMEOUT_DESKTOP_MENU = 200;

document.documentElement.style.setProperty('--duration-desktop-menu', `${ANIMATION_DURATION_DESKTOP_MENU}ms`);
document.documentElement.style.setProperty('--duration-mobile-drawer', `${ANIMATION_DURATION_MOBILE_DRAWER}ms`);

const BREAKPOINT = {
  DESKTOP: window.matchMedia('(min-width: 750px)'),
};

function onBreakpoint(media, cb) {
  if (!media) return;
  const handleChange = () => cb(media.matches);
  const remove = () => {
    media.removeEventListener('change', handleChange);
    cb = null;
  };

  handleChange();
  media.addEventListener('change', handleChange);
  return remove;
}

class HeaderMenu extends HTMLElement {
  static hoverTimeout = null;

  constructor() {
    super();
    this.details = this.querySelector('details');
    this.bodyLayer = document.querySelector('.js-body-layer');

    if (!this.details) return;

    // Use arrow functions to auto-bind context without overhead
    this.details.addEventListener('mouseenter', () => {
      clearTimeout(HeaderMenu.hoverTimeout);
      clearTimeout(this.closeTimeout);
      document.querySelectorAll('header-menu details[open]').forEach((details) => {
        if (details !== this.details) {
          details.classList.remove('menu-opening');
          details.removeAttribute('open');
        }
      });
      this.details.setAttribute('open', '');
      requestAnimationFrame(() => {
        this.details.classList.add('menu-opening');
        document.body.classList.add('header-menu-opening');
      });
      document.body.classList.add('header-menu-open');
    });

    this.details.addEventListener('mouseleave', () => {
      HeaderMenu.hoverTimeout = setTimeout(() => this.close(), HOVER_TIMEOUT_DESKTOP_MENU);
    });

    this.details.addEventListener('focusout', () => {
      // Small delay allows document.activeElement to update safely
      setTimeout(() => {
        if (!this.contains(document.activeElement)) this.close();
      }, 0);
    });
  }

  close() {
    this.details.classList.remove('menu-opening');
    if (!document.querySelector('header-menu details.menu-opening')) {
      document.body.classList.remove('header-menu-opening');
    }
    clearTimeout(this.closeTimeout);
    this.closeTimeout = setTimeout(() => {
      if (!this.details.classList.contains('menu-opening')) {
        this.details.removeAttribute('open');
        if (!document.querySelector('header-menu details[open]')) {
          document.body.classList.remove('header-menu-open');
        }
      }
    }, ANIMATION_DURATION_DESKTOP_MENU);
  }
}

customElements.define('header-menu', HeaderMenu);

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  // Mobile: Drawer menu - open/close menu items dropdown on click and close other dropdowns
  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    if (summary.classList.contains('js-menu-top-title')) {
      document.querySelectorAll('.js-menu-top-title').forEach((menu) => {
        if (summary != menu) {
          menu.parentNode.removeAttribute('open');
        }
      });
    }
  });

  if (summary.closest('header-drawer, menu-drawer')) return;

  // Footer menu - open/close menu items dropdown on click and close other dropdowns
  let footerDetails = summary.closest('.footer-block details');
  onBreakpoint(BREAKPOINT.DESKTOP, (open) => {
    if (!footerDetails) return;
    open ? footerDetails.setAttribute('open', '') : footerDetails.removeAttribute('open');
    open ? footerDetails.setAttribute('disabled', '') : footerDetails.removeAttribute('disabled');
    open ? summary.setAttribute('tabindex', '-1') : summary.removeAttribute('tabindex');
  });
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
}

/**
 * Escape for a plain disclosure — an inline expander, a facet group, the cart-drawer
 * note. Bound per disclosure, on keyup. These are not layers: FocusLayer defers to
 * whichever one is open inside the top layer, so Escape collapses this first and
 * dismisses the layer on the next press.
 */
function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this)),
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    event.target.name === 'plus' ? this.input.stepUp() : this.input.stepDown();
    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const min = parseInt(this.input.min);
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus?.classList.toggle('disabled', value <= min);
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus?.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details.mobile-facets__disclosure') || this.querySelector('details');
    this.navigationContainer = document.querySelector('.js-menu-drawer__navigation-container');
    this.isMobile = window.innerWidth < 750;
    this.isTablet = window.innerWidth < 990;
    this.viewportHeightTimeout = null;

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('click', this.onClick.bind(this));
    this.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: true });
    this.addEventListener('touchmove', this.onTouchMove.bind(this), { passive: true });
    this.addEventListener('touchend', this.onTouchEnd.bind(this), { passive: true });
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('button:not(.localization-selector):not(.mobile-facets__show-more)').forEach((button) =>
      button.addEventListener('click', this.onCloseButtonClick.bind(this)),
    );
  }

  setViewportHeight = () => {
    requestAnimationFrame(() => {
      document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    });
  };

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }
  onClick(event) {
    if (event.target.closest('.mobile-facets__show-more')) return;
    const summary = event.target.closest('summary');
    if (summary) {
      this.onSummaryClick(event);
      return;
    }
    if (event.target.classList.contains('menu-drawer__overlay')) {
      this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'));
    }
  }
  onSummaryClick(event) {
    const summaryElement = event.target.closest('summary');
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    const isSortDetails = detailsElement.classList.contains('js-sort-details');
    // Desktop sort is a local dropdown, not the filter drawer. Let <details> toggle natively.
    if (isSortDetails && window.innerWidth >= 750) {
      return;
    }

    function focusSubmenu() {
      detailsElement.querySelector('button')?.focus({ preventScroll: true });
      summaryElement.nextElementSibling.removeEventListener('transitionend', focusSubmenu);
    }
    // todo: consider remove this condition. not clear when we migth need it
    // when filetr sort by updated, the details element renders again
    // and does not indentified asmain anymore, which broke the opening logic
    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (this.isTablet) {
        debounce(this.setViewportHeight, 100);
      }
    } else {
      if (isSortDetails) {
        this.openMenuDrawer(summaryElement);
      }
      requestAnimationFrame(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', 'true');
        parentMenuElement && parentMenuElement.classList.add('submenu-open');

        requestAnimationFrame(() => {
          !reducedMotion || reducedMotion.matches
            ? focusSubmenu()
            : summaryElement.nextElementSibling.addEventListener('transitionend', focusSubmenu);
        });
      });
    }
  }

  openMenuDrawer(summaryElement) {
    requestAnimationFrame(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
      summaryElement.setAttribute('aria-expanded', 'true');
      document.body.classList.add(this.isMobile ? 'overflow-hidden-mobile' : 'overflow-hidden');
      this.mainDetailsToggle.getBoundingClientRect();

      this.pushMenuLayer(summaryElement);
    });
  }

  /**
   * One layer, rooted on the drawer's <details> so that an open submenu counts as a
   * nested disclosure: Escape then collapses the submenu first and dismisses the
   * drawer only when none is open.
   * @param {HTMLElement} summaryElement - the toggle to hand focus back to
   */
  pushMenuLayer(summaryElement) {
    FocusLayer.push(this.mainDetailsToggle, {
      contain: true,
      modal: true,
      lockScroll: false, // body.overflow-hidden(-mobile) above is this drawer's own lock
      trigger: summaryElement,
      initialFocus: summaryElement,
      // A KeyboardEvent: closeMenuDrawer resets aria-expanded only for those.
      onDismiss: () => this.closeMenuDrawer(new KeyboardEvent('keydown', { key: 'Escape' }), summaryElement),
    });
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    FocusLayer.pop(this.mainDetailsToggle, { returnFocus: elementToFocus || null });
    this.mainDetailsToggle.classList.remove('menu-opening');
    document.body.classList.remove(this.isMobile ? 'overflow-hidden-mobile' : 'overflow-hidden');
    this.closeAnimation(this.mainDetailsToggle);
    this.navigationContainer && (this.navigationContainer.scrollTop = 0);
    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onTouchStart(event) {
    if (!this.mainDetailsToggle.hasAttribute('open')) return;
    this.touchStartX = event.touches[0].clientX;
    this.touchStartY = event.touches[0].clientY;
  }

  onTouchMove(event) {
    if (!this.touchStartX || !this.mainDetailsToggle.hasAttribute('open')) return;
    this.touchCurrentX = event.touches[0].clientX;
    this.touchCurrentY = event.touches[0].clientY;
  }

  onTouchEnd(event) {
    if (!this.touchStartX || !this.touchCurrentX || !this.mainDetailsToggle.hasAttribute('open')) {
      this.resetTouch();
      return;
    }

    const diffX = this.touchStartX - this.touchCurrentX;
    const diffY = this.touchStartY - this.touchCurrentY;

    if (diffX > 50 && Math.abs(diffX) > Math.abs(diffY)) {
      this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'));
    }

    this.resetTouch();
  }

  resetTouch() {
    this.touchStartX = null;
    this.touchStartY = null;
    this.touchCurrentX = null;
    this.touchCurrentY = null;
  }

  onCloseButtonClick(event) {
    if (event.target.closest('.mobile-facets__show-more')) return;
    const detailsElement = event.currentTarget.closest('details');
    if (detailsElement === this.mainDetailsToggle) {
      this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'));
    } else {
      detailsElement && this.closeSubmenu(detailsElement);
    }
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    document.body.classList.remove('overflow-hidden', 'overflow-hidden-mobile');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    detailsElement.querySelector('summary')?.focus({ preventScroll: true });
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < ANIMATION_DURATION_MOBILE_DRAWER) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        // Focus back to the level that is still open. No trap: the drawer's own layer
        // is still on the stack and already contains focus.
        if (detailsElement.closest('details[open]')) {
          detailsElement.querySelector('summary')?.focus({ preventScroll: true });
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
    this.header = document.querySelector('.js-section-header');
    this.cachedHeaderBottom = null;
    this.debouncedOnResize = debounce(this.onResize, 150);
  }

  openMenuDrawer(summaryElement) {
    requestAnimationFrame(() => {
      this.cachedHeaderBottom = this.header.getBoundingClientRect().bottom;

      requestAnimationFrame(() => {
        document.documentElement.style.setProperty(
          '--header-bottom-position',
          `${parseInt(this.cachedHeaderBottom)}px`,
        );
      });

      this.header.classList.add('menu-open');
      this.mainDetailsToggle.classList.add('menu-opening');
      summaryElement.setAttribute('aria-expanded', 'true');
      document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
      this.mainDetailsToggle.getBoundingClientRect();

      this.pushMenuLayer(summaryElement);
    });

    window.addEventListener('resize', this.debouncedOnResize);
  }

  closeMenuDrawer(event, elementToFocus) {
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.debouncedOnResize);
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  onResize = () => {
    if (this.header) {
      requestAnimationFrame(() => {
        const newBottom = this.header.getBoundingClientRect().bottom;
        if (newBottom !== this.cachedHeaderBottom) {
          this.cachedHeaderBottom = newBottom;
          document.documentElement.style.setProperty('--header-bottom-position', `${parseInt(newBottom)}px`);
        }
      });
    }
    this.setViewportHeight();
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    // Escape belongs to FocusLayer — see the layer pushed in show().
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    this.getBoundingClientRect();

    FocusLayer.push(this, {
      contain: true,
      modal: true,
      lockScroll: false, // body.overflow-hidden above is this modal's own lock
      trigger: opener,
      initialFocus: this.querySelector('[role="dialog"]'),
      onDismiss: () => this.hide(),
    });
  }

  hide() {
    // Pop before removing [open]: FocusLayer drops layers that are not rendered,
    // and a dropped layer hands no focus back.
    FocusLayer.pop(this, { returnFocus: this.openedBy });

    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
  }
}
customElements.define('modal-dialog', ModalDialog);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));

    // Auto-load when scrolled into view
    this.setupIntersectionObserver();
  }

  setupIntersectionObserver() {
    const options = {
      root: null,
      rootMargin: '50px',
      threshold: 0.1,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !this.getAttribute('loaded')) {
          setTimeout(() => {
            if (!this.getAttribute('loaded')) {
              this.loadContent(false);
              observer.unobserve(this);
            }
          }, 100);
        }
      });
    }, options);

    observer.observe(this);

    this._observer = observer;
  }

  disconnectedCallback() {
    if (this._observer) {
      this._observer.disconnect();
    }
  }

  loadContent(focus = true) {
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = content.querySelector('video, iframe');
      if (deferredElement) {
        this.appendChild(deferredElement);
        if (focus) deferredElement.focus();
        if (deferredElement.nodeName == 'VIDEO') {
          deferredElement.play().catch((error) => {
            console.warn('Video autoplay failed:', error);
          });
        }
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', this.onVariantChange);
    this.selectedVariantState = this.querySelector('input:checked')
      ? !this.querySelector('input:checked').classList.contains('disabled')
      : true;
  }

  onVariantChange() {
    const previousVariantAvailability =
      this.currentVariant != undefined ? this.currentVariant.available : this.selectedVariantState;
    this.toggleAddButton(true, '', false);
    this.updateOptions();
    this.updateMasterId();
    this.updatePickupAvailability();
    this.removeErrorMessage();
    this.updateVariantStatuses();

    if (!this.currentVariant) {
      this.toggleAddButton(true, '', true);
      this.setUnavailable();
    } else {
      this.updateURL();
      this.updateVariantInput();
      this.updateShareUrl();
      if (this.currentVariant.available == true && previousVariantAvailability == true) {
        this.toggleAddButton(false, '', true);
      }
      this.renderProductInfo();
    }

    this.dispatchEvent(
      new CustomEvent('variant:changed', {
        detail: {
          currentVariant: this.currentVariant,
        },
        bubbles: true,
      }),
    );
  }

  updateOptions() {
    this.options = Array.from(this.querySelectorAll('select'), (select) => select.value);
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData().find((variant) => {
      return !variant.options
        .map((option, index) => {
          return this.options[index] === option;
        })
        .includes(false);
    });
  }

  updateURL() {
    if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
    const newUrl = new URL(this.dataset.url, window.location.origin);
    newUrl.searchParams.set('variant', this.currentVariant.id);

    // Preserve existing URL parameters (like source=recommendation, code=EARLY)
    const currentUrl = new URL(window.location);
    const currentParams = new URLSearchParams(currentUrl.searchParams);
    currentParams.delete('variant');
    currentParams.forEach((value, key) => {
      newUrl.searchParams.set(key, value);
    });
    window.history.replaceState({}, '', newUrl.toString());
  }

  updateShareUrl() {
    const shareButton = document.getElementById(`Share-${this.dataset.section}`);
    if (!shareButton || !shareButton.updateUrl) return;
    shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateVariantInput() {
    const productForms = document.querySelectorAll(
      `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`,
    );
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  updateVariantStatuses() {
    const selectedOptionOneVariants = this.variantData.filter(
      (variant) => this.querySelector(':checked').value === variant.option1,
    );
    const inputWrappers = [...this.querySelectorAll('.product-form__input')];
    inputWrappers.forEach((option, index) => {
      if (index === 0) return;
      const optionInputs = [...option.querySelectorAll('input[type="radio"], option')];
      const previousOptionSelected = inputWrappers[index - 1].querySelector(':checked').value;
      const availableOptionInputsValue = selectedOptionOneVariants
        .filter((variant) => variant.available && variant[`option${index}`] === previousOptionSelected)
        .map((variantOption) => variantOption[`option${index + 1}`]);
      this.setInputAvailability(optionInputs, availableOptionInputsValue);
    });
  }

  setInputAvailability(listOfOptions, listOfAvailableOptions) {
    listOfOptions.forEach((input) => {
      if (listOfAvailableOptions.includes(input.getAttribute('value'))) {
        input.innerText = input.getAttribute('value');
      } else {
        input.innerText = window.variantStrings.unavailable_with_option.replace('[value]', input.getAttribute('value'));
      }
    });
  }

  updatePickupAvailability() {
    const pickUpAvailability = document.querySelector('pickup-availability');
    if (!pickUpAvailability) return;

    if (this.currentVariant && this.currentVariant.available) {
      pickUpAvailability.fetchAvailability(this.currentVariant.id);
    } else {
      pickUpAvailability.removeAttribute('available');
      pickUpAvailability.innerHTML = '';
    }
  }

  removeErrorMessage() {
    this.removeAttribute('has-error');

    const section = this.closest('section');
    if (!section) return;

    const productForm = section.querySelector('product-form');
    if (productForm) productForm.handleErrorMessage();
  }

  renderProductInfo() {
    const requestedVariantId = this.currentVariant.id;
    const sectionId = this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section;

    fetch(`${this.dataset.url}?variant=${requestedVariantId}&section_id=product-info`)
      .then((response) => response.text())
      .then((responseText) => {
        // prevent unnecessary ui changes from abandoned selections
        if (this.currentVariant.id !== requestedVariantId) return;
        const html = new DOMParser().parseFromString(responseText, 'text/html');

        const fragment = document.createDocumentFragment();
        fragment.append(...html.body.children);

        const addButtonUpdated = fragment.getElementById(`ProductSubmitButton-product-info`);
        this.toggleAddButton(
          addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true,
          window.variantStrings.soldOut,
        );

        const destination = document.getElementById(`price-${this.dataset.section}`);
        const source = fragment.getElementById(`price-product-info`);
        const inventorySource = fragment.getElementById(`Inventory-product-info`);

        const inventoryDestination = document.getElementById(`Inventory-${this.dataset.section}`);

        // Updating Availability and delivery message
        const availabilityDestination = document.querySelector('.js-product-availability');
        const availabilitySource = fragment.querySelector('.js-product-availability');

        if (availabilitySource && availabilityDestination) {
          availabilityDestination.innerHTML = availabilitySource.innerHTML;
        }
        // Updating product price section
        const productPriceDestination = document.querySelector('.js-product-price');
        const productPriceSource = fragment.querySelector('.js-product-price');

        if (productPriceSource && productPriceDestination) {
          productPriceDestination.innerHTML = productPriceSource.innerHTML;
        }
        const productDiscountDestination = document.querySelector('.js-product-discounted-price');
        const productDiscountSource = fragment.querySelector('.js-product-discounted-price');
        if (productDiscountSource && productDiscountDestination) {
          productDiscountDestination.innerHTML = productDiscountSource.innerHTML;
        }

        if (source && destination) destination.innerHTML = source.innerHTML;
        if (inventorySource && inventoryDestination) inventoryDestination.innerHTML = inventorySource.innerHTML;

        const price = document.getElementById(`price-${this.dataset.section}`);

        if (price) price.classList.remove('hidden');

        if (inventoryDestination) inventoryDestination.classList.toggle('hidden', inventorySource.innerText === '');

        publish(PUB_SUB_EVENTS.variantChange, {
          data: {
            sectionId,
            html,
            variant: this.currentVariant,
          },
        });
      });
  }

  toggleAddButton(disable = true, text, modifyClass = true) {
    const productForm = document.getElementById(`product-form-${this.dataset.section}`);
    if (!productForm) return;
    const addButton = productForm.querySelector('[name="add"]');
    const addButtonText = productForm.querySelector('[name="add"] > span');
    if (!addButton) return;

    if (disable) {
      addButton.setAttribute('disabled', 'disabled');
      if (text) addButtonText.textContent = text;
    } else {
      addButton.removeAttribute('disabled');
      addButtonText.textContent = window.variantStrings.addToCart;
    }

    // Update shipping info visibility
    const shippingInfo = document.getElementById('shippingInfo');
    if (shippingInfo) {
      addButton.hasAttribute('disabled')
        ? shippingInfo.classList.add('hidden')
        : shippingInfo.classList.remove('hidden');
    }

    if (!modifyClass) return;
  }

  setUnavailable() {
    const button = document.getElementById(`product-form-${this.dataset.section}`);
    const addButton = button.querySelector('[name="add"]');
    const addButtonText = button.querySelector('[name="add"] > span');
    const price = document.getElementById(`price-${this.dataset.section}`);
    const inventory = document.getElementById(`Inventory-${this.dataset.section}`);

    if (!addButton) return;
    addButtonText.textContent = window.variantStrings.unavailable;
    if (price) price.classList.add('hidden');
    if (inventory) inventory.classList.add('hidden');
  }

  getVariantData() {
    this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
    return this.variantData;
  }
}

customElements.define('variant-selects', VariantSelects);

class VariantRadios extends VariantSelects {
  constructor() {
    super();
  }

  setInputAvailability(listOfOptions, listOfAvailableOptions) {
    listOfOptions.forEach((input) => {
      if (listOfAvailableOptions.includes(input.getAttribute('value'))) {
        input.classList.remove('is-disabled');
        input.removeAttribute('aria-disabled');
      } else {
        input.classList.add('is-disabled');
        input.setAttribute('aria-disabled', 'true');
      }
    });
  }

  updateOptions() {
    this.setAttribute('selected', '');
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    this.options = fieldsets.map((fieldset) => {
      return Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked).value;
    });
  }
}

customElements.define('variant-radios', VariantRadios);

class DropdownSelects extends HTMLElement {
  constructor() {
    super();
    this.variantRadiosParents = document.querySelectorAll('variant-radios');
    this.select = this.querySelector('.js-dropdown-select');
    this.selectedText = this.querySelector('.js-dropdown-selected .js-text');
    this.selectedToggle = this.querySelector('.js-dropdown-selected');
    this.options = this.querySelectorAll('.js-option');
    this.bodyLayer = document.querySelector('.body-layer');
    this.closeSelector = this.querySelector('.js-selector-close');

    this.addEventListener('click', this.onDropdownClick);
    this.addEventListener('keydown', this.onKeydown.bind(this));
    this.select?.addEventListener('click', this.onSelectClick.bind(this));
    document.addEventListener('click', this.onOutsideClick.bind(this));

    if (this.closeSelector) {
      this.closeSelector.addEventListener('click', this.closeSelectorEventListener.bind(this));
    }
  }

  openDropdown() {
    this.setAttribute('open', '');
    if (this.selectedToggle) this.selectedToggle.setAttribute('aria-expanded', 'true');

    if (!this.select || this.select.open) return;

    if (window.innerWidth < 750) {
      // Position the sheet before opening it
      this.select.classList.add('active');
      this.select.showModal?.();
    } else {
      this.select.show?.();
    }
  }

  closeDropdown(returnFocus = false) {
    this.removeAttribute('open');
    if (this.selectedToggle) {
      this.selectedToggle.setAttribute('aria-expanded', 'false');
      if (returnFocus) this.selectedToggle.focus();
    }

    if (!this.select) return;

    if (typeof this.select.close === 'function' && this.select.hasAttribute('open')) {
      this.select.close();
    }
    this.select.classList.remove('active');
  }

  closeSelectorEventListener() {
    this.closeDropdown();
  }

  onOutsideClick(e) {
    if (!this.contains(e.target)) {
      this.closeDropdown();
    }
  }

  onDropdownClick(e) {
    // Only the visible trigger may toggle the menu -- clicks on options,
    // their inner text/icons, or the close control must not (re)open it.
    if (this.select?.classList.contains('cart-items__options')) return;
    if (e.target.closest('.js-option') || e.target.closest('.js-selector-close')) return;
    if (!e.target.closest('.js-dropdown-selected')) return;

    if (this.hasAttribute('open')) {
      this.closeDropdown();
    } else {
      this.openDropdown();
    }
  }

  onSelectClick(e) {
    // If clicking the native dialog backdrop on mobile
    if (e.target === this.select) {
      this.closeDropdown();
      return;
    }

    if (this.tagName.toLowerCase() === 'dropdown-selects-options') {
      return;
    }

    // make sure we addressing click on the option element
    const targetOption = e.target.closest('.js-option');
    if (!targetOption) return;

    if (targetOption.classList.contains('dropdown__item--disabled')) {
      this.closeDropdown();
      return;
    }

    if (targetOption.dataset.optionValue) {
      // PDP sticky buy bar case -- not proceed with soldout variants
      if (targetOption.dataset.soldOut !== undefined) {
        return;
      }
      this.setAttribute('selected', '');
      this.selectedText.textContent = targetOption.dataset.optionValue;
      if (targetOption.querySelector('.js-text')?.classList.contains('sold-out')) {
        this.selectedText.classList.add('sold-out');
      } else {
        this.selectedText.classList.remove('sold-out');
      }
      this.variantRadiosParents.forEach((element) => {
        element.querySelectorAll('input').forEach((input) => {
          if (targetOption.dataset.optionValue === input.value) {
            input.click();
          }
        });
      });

      this.options.forEach((option) => {
        option.classList.remove('selected');
        option.setAttribute('aria-selected', 'false');
        option.setAttribute('tabindex', '-1');
      });
      targetOption.classList.add('selected');
      targetOption.setAttribute('aria-selected', 'true');
      targetOption.setAttribute('tabindex', '0');
      this.closeDropdown(true);
    }

    if (window.innerWidth < 750) {
      this.closeDropdown(true);
    }

    if (
      window.innerWidth < 750 &&
      (targetOption.classList.contains('dropdown__header') || targetOption.closest('.js-selector-close') != undefined)
    ) {
      this.removeAttribute('open');
    }
  }

  closeMobileSelector() {
    this.closeDropdown();
  }

  onKeydown(e) {
    const isOpen = this.hasAttribute('open');
    const availableOptions = Array.from(this.options).filter(
      (opt) => !opt.classList.contains('dropdown__item--disabled') && !opt.hasAttribute('data-sold-out'),
    );

    if (e.key === 'Escape') {
      if (isOpen) {
        this.closeDropdown(true);
      }
      return;
    }

    if (e.key === 'Enter' || e.key === ' ') {
      if (e.target === this.selectedToggle || e.target === this) {
        e.preventDefault();
        const nowOpen = !isOpen;
        if (nowOpen) {
          this.openDropdown();
        } else {
          this.closeDropdown();
        }

        if (nowOpen && availableOptions.length > 0) {
          const selectedOpt = availableOptions.find((opt) => opt.classList.contains('selected')) || availableOptions[0];
          selectedOpt.focus();
        }
      } else if (e.target.classList.contains('js-option')) {
        e.preventDefault();
        e.target.click();
      }
      return;
    }

    if (isOpen && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      e.preventDefault();
      const currentIndex = availableOptions.indexOf(e.target);
      let nextIndex = 0;

      if (e.key === 'ArrowDown') {
        nextIndex = currentIndex < availableOptions.length - 1 ? currentIndex + 1 : 0;
      } else {
        nextIndex = currentIndex > 0 ? currentIndex - 1 : availableOptions.length - 1;
      }

      if (availableOptions[nextIndex]) {
        availableOptions[nextIndex].focus();
      }
    }
  }
}

customElements.define('dropdown-selects', DropdownSelects);
class DropdownSelectsOptions extends DropdownSelects {
  constructor() {
    super();
    this.select?.addEventListener('click', this.onOptionUpdate.bind(this));
    this.cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
  }

  openDropdown() {
    super.openDropdown();
    const cartItem = this.closest('.cart-item');
    if (cartItem) cartItem.classList.add('cart-item--dropdown-open');
  }

  closeDropdown(returnFocus = false) {
    super.closeDropdown(returnFocus);
    const cartItem = this.closest('.cart-item');
    if (cartItem) cartItem.classList.remove('cart-item--dropdown-open');
  }

  onOptionUpdate(e) {
    e.stopPropagation();

    const targetOption = e.target.closest('.js-option');
    if (!targetOption) return;

    if (targetOption.classList.contains('dropdown__item--disabled')) {
      e.preventDefault();
      return;
    }

    let currentVariantId = targetOption.dataset.currentVariantId;
    let updatedVariantId = targetOption.dataset.updatedVariantId;
    let quantity = targetOption.dataset.quantity;
    let line = parseInt(targetOption.dataset.line);

    if (currentVariantId == updatedVariantId) {
      e.preventDefault();
      this.closeDropdown();
      return;
    }

    /* Shopify doesn't allow to update variant of line item, therefore:
      1. Remove the current line item variant
      2. Add a the selected variant to cart
    */

    this.replaceVariant(line, updatedVariantId, quantity);
  }

  replaceVariant(currentLineIndex, updatedVariantId, quantity) {
    this.closeDropdown();

    this.cartItems?.enableLoading(currentLineIndex);

    // Set flag to prevent extra services sync during variant replacement
    const extraServicesElements = document.querySelectorAll('extra-services');
    extraServicesElements.forEach((extraServices) => {
      extraServices.isVariantReplacementInProgress = true;
    });

    let selectedOption = this.select.querySelector(`[data-line="${currentLineIndex}"]`);
    const currentBody = JSON.stringify({
      line: currentLineIndex,
      quantity: 0,
      sections: this.cartItems?.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    const updatedBody = JSON.stringify({
      id: updatedVariantId,
      quantity: quantity,
      sections: this.cartItems?.getSectionsToRender().map((section) => section.section),
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body: currentBody } })
      .then(async (response) => {
        if (!response.ok) {
          const errorText = await response.text();
          console.error(`Cart change API error (${response.status}):`, errorText);
          throw new Error(`Failed to remove variant: ${response.status} ${response.statusText}`);
        }
        const responseText = await response.text();
        const responseJson = JSON.parse(responseText);

        // Add a delay to ensure the variant is removed from the cart
        await new Promise((resolve) => setTimeout(resolve, 200));

        return responseText;
      })
      .then(async (data) => {
        return fetch(`${routes.cart_add_url}`, { ...fetchConfig(), ...{ body: updatedBody } }).then(
          async (response) => {
            if (!response.ok) {
              const errorText = await response.text();
              console.error(`Cart add API error (${response.status}):`, errorText);
              throw new Error(`Failed to add variant: ${response.status} ${response.statusText}`);
            }
            const responseText = await response.text();
            const responseJson = JSON.parse(responseText);

            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'cart-items-add',
              cartData: responseJson,
            });

            return responseText;
          },
        );
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        this.cartItems?.renderContents(parsedState);

        if (window.updateWishlistStateImmediately) {
          window.updateWishlistStateImmediately();
        }
      })
      .catch((error) => {
        console.error('Error replacing variant:', error);
        // Fallback: If the atomic replace fails (e.g. remove succeeded but add failed),
        // we must resync the UI with the backend to prevent ghost items in the cart.
        window.location.reload();
      })
      .finally(() => {
        this.closeMobileSelector();
        this.cartItems?.disableLoading(currentLineIndex);
      });
  }
}

customElements.define('dropdown-selects-options', DropdownSelectsOptions);

class LazyLoadProducts extends HTMLElement {
  constructor() {
    super();
    this.collectionContext = this.getAttribute('data-collection');
    const rawHandles = this.getAttribute('data-handles') || '';
    this.allHandles = rawHandles.split('+').filter((h) => h.trim() !== '');

    // State management
    this.cursor = 0; // Tracks how many we have loaded
    this.batchSize = 20; // Shopify's safe limit for all_products lookup
    this.isLoading = false; // Prevent double-fetching

    this.listItemCustomClass = this.getAttribute('data-list-item-class') || '';
    // Which alternate collection template renders the cards:
    // 'list-products' = full card, 'list-products-light' = image + title only.
    this.view = this.getAttribute('data-view') || 'list-products';
    this.observer = null;
  }
  connectedCallback() {
    this.container = this.querySelector('.js-products-container');
    this.skeletons = this.container.querySelectorAll('skeleton-card-product');
    if (this.skeletons.length === 0) {
      const sentinel = document.createElement('div');
      sentinel.style.cssText = 'height: 1px; width: 100%; visibility: hidden;';
      sentinel.classList.add('js-hidden-sentinel');
      this.container.appendChild(sentinel);
      this.skeletons = [sentinel];
    }

    if (this.allHandles.length > 0) {
      this._initObserver();
    } else {
      this._removeSkeletons();
    }
  }

  _initObserver() {
    this.observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && !this.isLoading) {
          this._loadNextBatch();
        }
      },
      {
        rootMargin: '0px',
      },
    );

    const firstSkeleton = this.skeletons[0];
    if (firstSkeleton) {
      this.observer.observe(firstSkeleton);
    }
  }

  async _loadNextBatch() {
    if (this.cursor >= this.allHandles.length) {
      this._cleanup();
      return;
    }

    this.isLoading = true;

    try {
      // Slice the next chunk of handles
      const nextHandles = this.allHandles.slice(this.cursor, this.cursor + this.batchSize);
      const handlesParam = nextHandles.join('+');

      const campaign_code = new URLSearchParams(window.location.search).get('code');
      const baseUrl = window.routes.collections_url || '/collections';
      const url =
        `${baseUrl}/${this.collectionContext}?view=${encodeURIComponent(this.view)}` +
        `&limit=${this.batchSize}&type=${handlesParam}` +
        (campaign_code ? `&q=${campaign_code}` : '');

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status} ${response.statusText}`);
      }
      const htmlText = await response.text();

      const parser = new DOMParser();
      const parsedHTML = parser.parseFromString(htmlText, 'text/html');
      const parsedElements = parsedHTML.querySelectorAll('.js-fetched-item');
      const fragment = document.createDocumentFragment();

      const customClassArray = this.listItemCustomClass.split(' ').filter((cls) => cls.trim() !== '');
      const insertionPoint = this.skeletons[0];
      parsedElements.forEach((element) => {
        if (customClassArray.length) element.classList.add(...customClassArray);
        fragment.appendChild(element);
      });
      window.updateWishlistStateImmediately?.(fragment);
      // Append new items BEFORE the skeletons
      // This pushes the skeletons down, keeping them at the bottom as the new sentinel
      this.container.insertBefore(fragment, insertionPoint);

      // Update State
      this.cursor += nextHandles.length;
      if (this.cursor >= this.allHandles.length) {
        this._cleanup();
      }
    } catch (error) {
      console.error('Fetch error:', error);
      this._cleanup();
    } finally {
      this.isLoading = false;
    }
  }

  _cleanup() {
    this._removeSkeletons();
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  _removeSkeletons() {
    this.skeletons.forEach((el) => el.remove());
    this.skeletons = [];
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}
customElements.define('lazy-load-products', LazyLoadProducts);

class PopupRedirect extends ModalDialog {
  constructor() {
    super();
    this.currentMarketHandle = redirectData.current_market.handle;
    this.id = this.getAttribute('id');

    this.view1 = this.querySelector('#view-1');
    this.view2 = this.querySelector('#view-2');
    this.modalContent = this.querySelector('.country-redirect-popup-modal__content');
    this.switchToOtherViewButton = this.querySelector('#switch-to-other-view');
    this.backToFirstViewButton = this.querySelector('#back-to-first-view');
    this.continueCurrentMarketButton = this.querySelector('#continue-current-market');

    this.initViewSwitching();
    this.initContinueMarketButton();

    // Check if the popup should be displayed
    if (sessionStorage.getItem(this.id) === 'true') {
      return;
    } else if (sessionStorage.getItem(this.id) === null) {
      this.fetchBrowserContextSuggestions();
    }
  }

  initViewSwitching() {
    // Switch to the second view
    this.switchToOtherViewButton.addEventListener('click', () => {
      this.view1.style.display = 'none';
      this.view2.style.display = 'block';
      this.modalContent.classList.add('view-2');
      this.modalContent.classList.remove('view-1');
    });

    // Go back to the first view
    this.backToFirstViewButton.addEventListener('click', () => {
      this.view2.style.display = 'none';
      this.view1.style.display = 'block';
      this.modalContent.classList.add('view-1');
      this.modalContent.classList.remove('view-2');
    });
  }

  initContinueMarketButton() {
    this.continueCurrentMarketButton.addEventListener('click', () => {
      sessionStorage.setItem(this.id, 'true');

      this.removeAttribute('open');
    });
  }

  // Method to fetch the browsing context suggestions
  fetchBrowserContextSuggestions() {
    const url = `${location.origin}/browsing_context_suggestions.json`;
    fetch(url)
      .then((response) => response.json())
      .then((data) => {
        this.contextData = data.detected_values;
        this.userCountryHandle = this.contextData.country.handle.toLowerCase();
        this.userCountryName = this.contextData.country_name;
        this.isInternationalMarket =
          this.currentMarketHandle === 'international' &&
          redirectData.markets.international.supported_countries.includes(this.userCountryHandle);

        if (this.currentMarketHandle === this.userCountryHandle || this.isInternationalMarket) return;

        // Show view-1
        this.view2.style.display = 'none';
        this.view1.style.display = 'block';

        this.setAttribute('open', '');
      })
      .catch((error) => {
        console.error(error);
      });
  }
}

customElements.define('popup-redirect', PopupRedirect);

// ==========================================================================
// REDESIGN GLOBAL UTILITIES & EVENT DELEGATORS
// ==========================================================================

// Global click delegation for interactive pills/swatches/tabs (data-toggle-pill)
document.addEventListener('click', (event) => {
  const pill = event.target.closest('[data-toggle-pill]');
  if (!pill) return;

  const mode = pill.getAttribute('data-toggle-pill'); // 'single' (default) or 'multi'
  const isMulti = mode === 'multi';

  if (isMulti) {
    const isActive = pill.classList.contains('is-active');
    pill.classList.toggle('is-active', !isActive);
    pill.setAttribute('aria-pressed', isActive ? 'false' : 'true');
    if (pill.hasAttribute('aria-selected')) {
      pill.setAttribute('aria-selected', isActive ? 'false' : 'true');
    }
  } else {
    // Single-select mode: siblings with the same group name or within parent
    const groupName = pill.getAttribute('data-toggle-pill-group');
    let parent = pill.parentElement;
    if (groupName) {
      const groupContainer = document.querySelector(`[data-pill-group="${groupName}"]`);
      if (groupContainer) parent = groupContainer;
    }

    if (parent) {
      const siblings = parent.querySelectorAll('[data-toggle-pill]');
      siblings.forEach((sib) => {
        if (!groupName || sib.getAttribute('data-toggle-pill-group') === groupName) {
          sib.classList.remove('is-active');
          sib.setAttribute('aria-pressed', 'false');
          sib.removeAttribute('aria-selected');
        }
      });
    }

    pill.classList.add('is-active');
    pill.setAttribute('aria-pressed', 'true');
    pill.setAttribute('aria-selected', 'true');
  }
});

class BackLink extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', this.onClick.bind(this));
    this.addEventListener('keydown', this.onKeyDown.bind(this));
  }

  connectedCallback() {
    if (!this.hasAttribute('tabindex')) this.setAttribute('tabindex', '0');
    if (!this.hasAttribute('role')) this.setAttribute('role', 'button');
  }

  onKeyDown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      this.onClick(event);
    }
  }

  onClick(event) {
    event.preventDefault();
    const link = this.querySelector('a') || this;
    const fallback = link.getAttribute('href') || '/';

    let isSameOrigin = false;
    try {
      isSameOrigin = new URL(document.referrer).origin === window.location.origin;
    } catch (e) {}

    if (isSameOrigin) {
      window.history.back();
    } else {
      window.location.href = fallback;
    }
  }
}

customElements.define('back-link', BackLink);
